import { ReactComponent as MoreIcon } from '@/assets/images/svg/more.svg';
import { ChainId, NETWORK_CONFIG } from '@/constant';
import { useDataAnchoringTokenContract } from '@/hooks/useContract';
import application from '@/stores/application';
import serverConfig from '@/stores/serverConfig';
import user from '@/stores/user';
import { formatHashValue } from '@/utils';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

interface IncubatorDrawerProps {}

const StyledDrawer = styled(Drawer)`
    & .MuiDrawer-paper {
        position: fixed;
        top: 80px;
        right: 0;
        height: 100%;
        z-index: 40;
        transform: none;
        width: 300px;
        transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

        ${({ theme }) => theme.mediaWidth.upToSmall`
            top: 60px;
            width: 80vw;
        `}
    }

    & .MuiBackdrop-root {
        display: none;
    }
`;

const Image = styled.div<{ src: string }>`
    width: 100%;
    height: 370px;
    background-image: url(${(props) => props.src});
    background-size: cover;
    background-position: center;
    border-radius: 10px 10px 0 0;
    overflow: hidden;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 300px;
    `}
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 50;
    height: 40px;
    width: 40px;
    background-color: white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    transition: background-color 0.15s ease-in-out;

    ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
    `};

    svg {
        color: #000;
    }

    &:hover {
        background-color: #fff;
    }
`;

export const IncubatorDrawer: React.FC<IncubatorDrawerProps> = observer(() => {
    const location = useLocation();
    const [incubatedLazbubu, setIncubatedLazbubu] = useState(0);
    const dataAnchoringTokenContract = useDataAnchoringTokenContract();

    useEffect(() => {
        if (dataAnchoringTokenContract) {
            dataAnchoringTokenContract.currentTokenId().then((count) => {
                setIncubatedLazbubu(Number(count));
            });
        }
    }, [dataAnchoringTokenContract]);

    const handleDrawerClose = () => {
        application.closeIncubatorDrawer();
    };

    const onClickDat = () => {
        const explorer =
            NETWORK_CONFIG[user.chainId || ChainId.testnet].explore;
        window.open(
            `${explorer}address/${serverConfig?.config?.contracts.dataAnchoringTokenAddress}`,
            '_blank',
        );
    };

    const handleDrawerOpen = () => {
        application.openIncubatorDrawer();
    };

    if (location.pathname.indexOf('/agent/') !== -1) {
        return null;
    }

    return (
        <>
            {!application.isIncubatorDrawerOpen &&
                !location.pathname.includes('/lazbubu') && (
                    <StyledIconButton
                        onClick={handleDrawerOpen}
                        aria-label="open drawer"
                    >
                        <MoreIcon />
                    </StyledIconButton>
                )}
            <StyledDrawer
                anchor="right"
                open={application.isIncubatorDrawerOpen}
                onClose={handleDrawerClose}
                variant="persistent"
                className="[&_.MuiDrawer-paper]:w-[350px] max-md:[&_.MuiDrawer-paper]:w-[250px] [&_.MuiDrawer-paper]:bg-white [&_.MuiDrawer-paper]:shadow-lg [&_.MuiDrawer-paper]:border-none [&_.MuiDrawer-paper]:absolute [&_.MuiDrawer-paper]:top-[80px] [&_.MuiDrawer-paper]:right-0 [&_.MuiDrawer-paper]:h-full [&_.MuiDrawer-paper]:z-40 [&_.MuiDrawer-paper]:transform-none [&_.MuiDrawer-paper]:transition-transform [&_.MuiDrawer-paper]:duration-300 [&_.MuiDrawer-paper]:ease-in-out [&_.MuiBackdrop-root]:hidden"
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={handleDrawerClose}
                    className="absolute top-8 left-4 z-50 bg-white/80 hover:bg-white shadow-md"
                    size="small"
                >
                    <KeyboardDoubleArrowRightIcon className="text-gray-600" />
                </IconButton>

                {/* Incubator Image */}
                <Box className="flex justify-center flex-col items-center gap-4 pb-6 bg-gradient-to-b from-cyan-50 to-white">
                    <div className="w-full h-auto relative">
                        <Image
                            src={require('@/assets/images/lazbubu-incubator.png')}
                        />
                        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-white"></div>
                    </div>
                    <img
                        className="w-[160px] h-[160px] rounded-[20px] shadow-lg mt-[-100px] z-10 max-md:w-[120px] max-md:h-[120px]"
                        src={require('@/assets/images/lazbubu-incubator.png')}
                        alt="Lazbubu Incubator"
                    />
                    <Typography
                        component="div"
                        className="font-semibold text-black text-[18px]"
                    >
                        Lazbubu Incubator
                    </Typography>
                </Box>

                {/* Incubator Details */}
                <Box className="p-6 flex-1 ">
                    {/* Basic Information */}
                    <Box className="mb-6">
                        <Box className="space-y-3">
                            <Box className="flex justify-between items-center">
                                <Typography className="text-[14px] text-gray">
                                    Owner
                                </Typography>
                                <Typography className="text-[14px] font-medium text-blue-600">
                                    LazAI
                                </Typography>
                            </Box>
                            <Box className="flex justify-between items-center">
                                <Typography className="text-[14px] text-gray">
                                    Birthday
                                </Typography>
                                <Typography className="text-[14px] font-medium">
                                    August 20, 2025
                                </Typography>
                            </Box>
                            <Box className="flex justify-between items-center">
                                <Typography className="text-[14px] text-gray">
                                    Incubated Lazbubu
                                </Typography>
                                <Typography className="text-[14px] font-medium">
                                    {incubatedLazbubu} DAT
                                </Typography>
                            </Box>
                            <Box className="flex justify-between items-center">
                                <Typography className="text-[14px] text-gray">
                                    Onchain DAT
                                </Typography>
                                <Typography
                                    onClick={onClickDat}
                                    className="text-[14px] font-medium text-blue-600 hover:underline cursor-pointer"
                                >
                                    {formatHashValue(
                                        serverConfig?.config?.contracts
                                            .dataAnchoringTokenAddress,
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </StyledDrawer>
        </>
    );
});

export default IncubatorDrawer;
